// Open tabs pick up new versions. A tab left open across a deploy keeps
// running the old code (an old Case Plan, old fonts) until it reloads.
// We compare this build's id with /version.json when the tab comes back
// into view and every few minutes; once a newer build is live, the next
// page change reloads the app. Nothing reloads under someone typing.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const CHECK_EVERY_MS = 5 * 60 * 1000;
let newerBuildLive = false;

async function check(): Promise<void> {
  if (newerBuildLive || import.meta.env.DEV) return;
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return;
    const { build } = (await r.json()) as { build?: string };
    if (build && build !== __BUILD_ID__) newerBuildLive = true;
  } catch {
    // Offline or blocked: try again next time.
  }
}

/** Mount once inside the router. */
export function useFreshBuild(): void {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    void check();
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, []);
  // A page change is a natural moment to swap in the new version.
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (newerBuildLive) window.location.reload();
  }, [pathname]);
}
